import { useState } from 'react';
import type { FolderResponseDto } from '@inference.engine/workspace-api-client/web';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import {
  ChevronDownIcon,
  DownloadIcon,
  ImageIcon,
  ListChecksIcon,
  PencilIcon,
  Trash2Icon,
} from 'lucide-react';
import { useDeleteFolder, useDownloadFolder } from '@/features/manage-project-folders';
import { notificationService } from '@/lib/services/notification.service';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { ScopedPermission } from '@/shared/ui';
import { useModalState } from '@/shared/hooks';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { FolderApprovalChainDialog } from '../FolderApprovalChainDialog';
import { FolderThumbnailPicker } from '../FolderThumbnailPicker';
import { useTranslation } from 'react-i18next';

interface FolderActionsMenuProps {
  projectId: string;
  folder: FolderResponseDto;
  /**
   * Opens the folder's editor. The header owns it so that a double-click on
   * the title reaches the same dialog this item does.
   */
  onEdit: () => void;
  /** Runs after a delete succeeds, so the caller can leave the folder's route. */
  onDeleted: () => void;
  /** The caller's `useApprovalChainsEnabled()`: the chain item hides with every other chain surface. */
  approvalChainsEnabled: boolean;
}

/**
 * Rename, thumbnail, approval chain, download and delete for the open folder.
 *
 * These used to hang off each row of the folders table. The rail replaced that
 * table, and a tree row is too narrow to carry a menu, so they moved to the
 * header of whichever folder is open.
 */
export function FolderActionsMenu(props: FolderActionsMenuProps) {
  const { t } = useTranslation('projects');
  const { t: tCommon } = useTranslation('common');

  const { folder, projectId } = props;
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const thumbnailModal = useModalState();
  const approvalChainModal = useModalState();
  // Success is handled at mutation level, not from `mutate`: this menu belongs
  // to the folder being deleted, so it unmounts with it, and TanStack drops the
  // callbacks passed to `mutate` when their component goes first.
  const deleteMutation = useDeleteFolder({
    projectId,
    onDeleted: () => {
      notificationService.showSuccess(t('projectDetail.folderActionsMenu.folderDeleted'));
      setIsDeleteOpen(false);
      props.onDeleted();
    },
  });
  const downloadFolder = useDownloadFolder({ platformProjectId: projectId });

  const handleConfirmDelete = () => {
    deleteMutation.mutate(folder.id, {
      // A failed delete leaves the folder, and the menu, exactly where they
      // were, so this one is safe to keep on the call.
      onError: (error: unknown) => {
        const { detail } = parseProblemDetail(
          error,
          t('projectDetail.folderActionsMenu.couldNotDeleteFolder'),
        );
        notificationService.showError(detail);
      },
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-mini"
            aria-label={t('projectDetail.folderActionsMenu.folderActions')}
            className="text-foreground-subtle"
          >
            <ChevronDownIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectUploadAssets]}>
            <DropdownMenuItem onSelect={props.onEdit}>
              <PencilIcon className="mr-2 size-4" />
              {t('projectDetail.folderActionsMenu.renameFolder')}
            </DropdownMenuItem>
          </ScopedPermission>
          <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectUploadAssets]}>
            <DropdownMenuItem onSelect={thumbnailModal.open}>
              <ImageIcon className="mr-2 size-4" />
              {t('projectDetail.folderActionsMenu.changeThumbnail')}
            </DropdownMenuItem>
          </ScopedPermission>
          {props.approvalChainsEnabled && (
            <ScopedPermission permissions={[PROJECT_PERMISSIONS.ManageApprovalChains]}>
              <DropdownMenuItem onSelect={approvalChainModal.open}>
                <ListChecksIcon className="mr-2 size-4" />
                {t('projectDetail.folderActionsMenu.setApprovalChain')}
              </DropdownMenuItem>
            </ScopedPermission>
          )}
          <DropdownMenuItem onSelect={() => downloadFolder.mutate(folder)}>
            <DownloadIcon className="mr-2 size-4" />
            {t('projectDetail.folderActionsMenu.download')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectUploadAssets]}>
            <DropdownMenuItem variant="destructive" onSelect={() => setIsDeleteOpen(true)}>
              <Trash2Icon className="mr-2 size-4" />
              {t('projectDetail.folderActionsMenu.deleteFolder')}
            </DropdownMenuItem>
          </ScopedPermission>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteConfirmationModal
        data-testid="delete-folder-confirmation"
        open={isDeleteOpen}
        isDeleting={deleteMutation.isPending}
        resourceType={tCommon('components.deleteConfirmationModal.folder')}
        resourceName={folder.name}
        warningMessage={
          folder.foldersCount > 0
            ? t('projectDetail.folderActionsMenu.thisActionCannotBeUndoneWithSubfolders')
            : t('projectDetail.folderActionsMenu.thisActionCannotBeUndone')
        }
        onCancel={() => setIsDeleteOpen(false)}
        onConfirmDelete={handleConfirmDelete}
      />

      {approvalChainModal.isMounted && (
        <FolderApprovalChainDialog
          key={approvalChainModal.instanceKey}
          projectId={projectId}
          folder={folder}
          open={approvalChainModal.isOpen}
          onClose={approvalChainModal.close}
        />
      )}

      {thumbnailModal.isMounted && (
        <FolderThumbnailPicker
          key={thumbnailModal.instanceKey}
          projectId={projectId}
          folder={folder}
          open={thumbnailModal.isOpen}
          onClose={thumbnailModal.close}
        />
      )}
    </>
  );
}
